/**
 * DemoResetBanner — "this deployment empties itself, and here is when".
 *
 * WHY IT EXISTS
 * -------------
 * A demo node truncates every AI event, alert and attendance row on a cycle
 * (DEMO_RESET_ENABLED / DEMO_RESET_INTERVAL_MINUTES on the node). Watching that happen with
 * no explanation is indistinguishable from a fault: alerts somebody was part-way through
 * reading vanish, every counter drops to zero, and the honest reading of that is "the product
 * lost my alerts". Stating the cycle before it happens — and counting down to it — turns the
 * disappearance into something the page predicted.
 *
 * WHERE IT LIVES
 * --------------
 * The app title bar, on every page. It first sat inside the alert panel, which was wrong
 * twice over: the wipe clears attendance and reports too, not just the board, and a notice
 * about the whole deployment does not belong inside one panel on one page.
 *
 * THE CLOCK
 * ---------
 * The countdown ticks locally but the deadline is the NODE's (`nextResetAt`), re-announced
 * after every wipe — so it cannot drift against the real timer, and reaching zero does not
 * silently restart it. Zero shows "clearing now" and HOLDS there until the node says
 * otherwise, because the wipe genuinely has not happened until the node reports it.
 */
import { useEffect, useState } from 'react';
import { ArrowPathIcon } from '@heroicons/react/24/outline';
import type { DemoResetInfo } from '../../api/contracts';

/** Remaining time as "9s" (< 1 min) or "m:ss". */
function fmtRemaining(ms: number): string {
  const total = Math.max(0, Math.ceil(ms / 1000));
  if (total < 60) return `${total}s`;
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

export interface DemoResetBannerProps {
  info: DemoResetInfo | null | undefined;
  className?: string;
  /**
   * Drop the explanatory sentence and keep the countdown pill — for a narrow title bar.
   * The full text is still available as the element's tooltip, so nothing is lost, only
   * moved behind a hover.
   */
  compact?: boolean;
}

export default function DemoResetBanner({ info, className = '', compact = false }: DemoResetBannerProps) {
  // Hooks run unconditionally (rules of hooks) — the render below is what opts out.
  const [now, setNow] = useState(() => Date.now());
  const nextResetAt = info?.nextResetAt ?? null;
  useEffect(() => {
    if (nextResetAt == null) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [nextResetAt]);

  if (!info?.enabled) return null;

  const remaining = nextResetAt == null ? null : nextResetAt - now;
  const every = info.intervalMinutes ? `every ${info.intervalMinutes} min` : 'on a timer';
  const full = `Demo site — all alerts, events and attendance records on this system are cleared automatically ${every}.`;

  return (
    <div
      className={`flex min-w-0 items-center gap-2 rounded-lg border border-amber-400/40 bg-amber-400/10 px-2.5 py-1 ${className}`}
      title={full}
    >
      <ArrowPathIcon className="h-4 w-4 shrink-0 text-amber-500" />
      {!compact && (
        <span className="min-w-0 truncate text-[11px] leading-tight text-foreground">
          <span className="font-bold">Demo site</span> — alerts clear automatically {every}
        </span>
      )}
      {compact && <span className="text-[11px] font-bold leading-tight text-foreground">Demo site</span>}
      {remaining != null && (
        <span className="shrink-0 rounded bg-amber-500 px-1.5 py-0.5 font-mono text-[10px] font-bold tabular-nums text-amber-950">
          {remaining > 0 ? `clears in ${fmtRemaining(remaining)}` : 'clearing now…'}
        </span>
      )}
    </div>
  );
}
